export default function Sustainability() {

  const contributionTypes = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'دعم مادي مباشر',
      description: 'تمويل جزء من تكاليف تطوير المشروع أو إطلاقه',
      color: 'from-[#FF6B6B] to-[#FF8E53]',
      examples: ['منحة تأسيسية', 'قرض ميسّر', 'تمويل مرحلي']
    },
    {
      icon: 'ri-home-heart-line',
      title: 'احتضان وبيئة عمل',
      description: 'توفير المساحة والموارد والأدوات اللازمة لنمو المشروع',
      color: 'from-[#4ECDC4] to-[#44A08D]',
      examples: ['مكتب أو مساحة عمل', 'تجهيزات وأدوات', 'خدمات لوجستية']
    },
    {
      icon: 'ri-user-star-line',
      title: 'إرشاد وخبرات',
      description: 'ربط الشاب بمرشدين ومتخصصين وشبكة علاقات واسعة',
      color: 'from-[#667EEA] to-[#764BA2]',
      examples: ['مرشد متخصص', 'ورش عمل', 'شبكة مستثمرين']
    },
    {
      icon: 'ri-links-line',
      title: 'شراكات وفرص',
      description: 'فتح أبواب الشراكات المؤسسية والفرص التجارية للمشروع',
      color: 'from-[#F7971E] to-[#FFD200]',
      examples: ['عملاء أوائل', 'شراكات حكومية', 'اتفاقيات تجارية']
    }
  ];

  const milestones = [
    {
      year: 'السنة الأولى',
      label: 'بناء المحفظة',
      description: 'احتضان أولى المشاريع الريادية وتأسيس نموذج الحصص',
      projects: '3–5 مشاريع',
      icon: 'ri-seedling-line',
      color: 'bg-gradient-to-br from-green-400 to-emerald-500'
    },
    {
      year: 'السنة الثالثة',
      label: 'نمو المحفظة',
      description: 'بعض المشاريع تبدأ في تحقيق إيرادات وتوزيع عوائد',
      projects: '10–15 مشروعاً',
      icon: 'ri-plant-line',
      color: 'bg-gradient-to-br from-blue-400 to-indigo-500'
    },
    {
      year: 'السنة الخامسة',
      label: 'أصول حقيقية',
      description: 'محفظة متنوعة من الحصص في مشاريع ناجحة ومتنامية',
      projects: '+25 مشروعاً',
      icon: 'ri-building-4-line',
      color: 'bg-gradient-to-br from-purple-400 to-pink-500'
    }
  ];

  const strategies = [
    {
      icon: 'ri-map-pin-line',
      title: 'التوسع الجغرافي',
      description: 'فتح فروع في مدن ومناطق مختلفة لتوسيع نطاق الأثر',
      color: 'from-[#FF6B6B] to-[#FF8E53]'
    },
    {
      icon: 'ri-file-copy-line',
      title: 'نسخ النموذج',
      description: 'تكرار النموذج الناجح في مواقع جديدة بنفس المعايير',
      color: 'from-[#4ECDC4] to-[#44A08D]'
    },
    {
      icon: 'ri-building-line',
      title: 'إطلاق فروع',
      description: 'إنشاء فروع جديدة تحت نفس العلامة التجارية',
      color: 'from-[#F7971E] to-[#FFD200]'
    },
    {
      icon: 'ri-award-line',
      title: 'ترخيص النموذج',
      description: 'منح تراخيص للمؤسسات الأخرى لتطبيق النموذج',
      color: 'from-[#667EEA] to-[#764BA2]'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white to-[#FFF5F3]">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            الاستدامة والتوسع
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            استراتيجية النمو والتوسع المستدام
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {strategies.map((strategy, index) => (
            <div
              key={index}
              className="group bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${strategy.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${strategy.icon} text-white text-3xl`}></i>
              </div>
              <h4 className="font-bold text-xl text-gray-900 mb-3">
                {strategy.title}
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {strategy.description}
              </p>
            </div>
          ))}
        </div>

        {/* ===== INCUBATION MODEL SECTION ===== */}
        <div className="mb-20">
          {/* Section Header */}
          <div className="text-center mb-10 lg:mb-14">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-5">
              <i className="ri-percent-line"></i>
              نموذج الاستدامة الذكي
            </div>
            <h3 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-4 px-2">
              الجمعية شريكة في نجاح الشباب
            </h3>
            <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-2">
              كل مشروع ريادي يحتضنه ديوان هو فرصة لبناء أصول حقيقية للجمعية —
              حصة بالملكية مقابل دعم متنوع ومتفاوت حسب طبيعة كل مشروع ومساهمة الجمعية فيه
            </p>
          </div>

          {/* How it works - Flow */}
          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-3xl p-6 lg:p-10 mb-14 shadow-sm">
            <h4 className="text-xl font-bold text-gray-800 text-center mb-8">كيف يعمل النموذج؟</h4>

            {/* Mobile: vertical steps */}
            <div className="flex flex-col gap-3 lg:hidden">
              {[
                { icon: 'ri-user-heart-line', color: 'from-[#FF6B6B] to-[#FF8E53]', title: 'شاب لديه فكرة', desc: 'يأتي بمشروعه أو فكرته الريادية' },
                { icon: 'ri-home-heart-line', color: 'from-[#4ECDC4] to-[#44A08D]', title: 'ديوان يحتضنه', desc: 'دعم مالي أو بيئة أو خبرات أو شراكات' },
                { icon: 'ri-shake-hands-line', color: 'from-[#667EEA] to-[#764BA2]', title: 'اتفاقية حصة', desc: 'نسبة ملكية متفاوتة حسب حجم المساهمة' },
                { icon: 'ri-line-chart-line', color: 'from-[#F7971E] to-[#FFD200]', title: 'المشروع ينمو', desc: 'عوائد وأرباح تعود للجمعية تلقائياً' },
                { icon: 'ri-safe-line', color: 'from-emerald-500 to-green-600', title: 'أصول الجمعية', desc: 'محفظة استثمارية ريادية متنامية' },
              ].map((step, i) => (
                <div key={i}>
                  <div className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                    <div className={`w-12 h-12 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                      <i className={`${step.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{step.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                    </div>
                    <div className="mr-auto text-lg font-bold text-gray-300">#{i + 1}</div>
                  </div>
                  {i < 4 && <div className="flex justify-center py-1 text-gray-300 text-xl">↓</div>}
                </div>
              ))}
            </div>

            {/* Desktop: horizontal flow */}
            <div className="hidden lg:flex items-center justify-center gap-0">
              {[
                { icon: 'ri-user-heart-line', color: 'from-[#FF6B6B] to-[#FF8E53]', title: 'شاب لديه فكرة', desc: 'يأتي بمشروعه أو فكرته الريادية' },
                { icon: 'ri-home-heart-line', color: 'from-[#4ECDC4] to-[#44A08D]', title: 'ديوان يحتضنه', desc: 'دعم مالي أو بيئة أو خبرات أو شراكات' },
                { icon: 'ri-shake-hands-line', color: 'from-[#667EEA] to-[#764BA2]', title: 'اتفاقية حصة', desc: 'نسبة ملكية متفاوتة حسب حجم المساهمة' },
                { icon: 'ri-line-chart-line', color: 'from-[#F7971E] to-[#FFD200]', title: 'المشروع ينمو', desc: 'عوائد وأرباح تعود للجمعية تلقائياً' },
                { icon: 'ri-safe-line', color: 'from-emerald-500 to-green-600', title: 'أصول الجمعية', desc: 'محفظة استثمارية ريادية متنامية' },
              ].map((step, i) => (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center text-center w-44">
                    <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-3 shadow-lg`}>
                      <i className={`${step.icon} text-white text-2xl`}></i>
                    </div>
                    <p className="font-bold text-gray-800 text-sm">{step.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{step.desc}</p>
                  </div>
                  {i < 4 && <div className="text-gray-300 text-3xl mx-2">›</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Contribution Types */}
          <div className="mb-14">
            <h4 className="text-xl font-bold text-gray-800 text-center mb-8">أنواع مساهمة الجمعية <span className="text-gray-400 font-normal text-base">(تحدد نسبة الحصة)</span></h4>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contributionTypes.map((type, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-12 h-12 bg-gradient-to-br ${type.color} rounded-xl flex items-center justify-center mb-4`}>
                    <i className={`${type.icon} text-white text-xl`}></i>
                  </div>
                  <h5 className="font-bold text-gray-900 mb-2">{type.title}</h5>
                  <p className="text-sm text-gray-500 mb-4 leading-relaxed">{type.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {type.examples.map((ex, i) => (
                      <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{ex}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Equity Note */}
          <div className="bg-gradient-to-l from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-8 mb-14">
            <div className="flex flex-col lg:flex-row items-start gap-6">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md">
                <i className="ri-scales-line text-white text-2xl"></i>
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-3">نسبة الحصة — مرنة وعادلة</h4>
                <p className="text-gray-600 leading-relaxed mb-4">
                  لا توجد نسبة ثابتة لجميع المشاريع — فالحصة تُحدَّد بالتفاوض بناءً على عدة عوامل:
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { icon: 'ri-money-dollar-circle-line', text: 'حجم الدعم المادي المُقدَّم' },
                    { icon: 'ri-time-line', text: 'مدة فترة الاحتضان' },
                    { icon: 'ri-bar-chart-line', text: 'مرحلة المشروع عند الدخول' },
                    { icon: 'ri-tools-line', text: 'نوع وقيمة الموارد المُوفَّرة' },
                    { icon: 'ri-team-line', text: 'الشبكة والخبرات المُسخَّرة' },
                    { icon: 'ri-lightbulb-line', text: 'إمكانية النمو والعائد المتوقع' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-amber-100">
                      <i className={`${item.icon} text-amber-500`}></i>
                      <span className="text-sm text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline - Asset Growth */}
          <div className="mb-14">
            <h4 className="text-xl font-bold text-gray-800 text-center mb-10">مسار تراكم الأصول عبر الزمن</h4>
            <div className="relative">
              {/* Line */}
              <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-green-300 via-blue-300 to-purple-400"></div>
              <div className="grid lg:grid-cols-3 gap-8">
                {milestones.map((m, index) => (
                  <div key={index} className="flex flex-col items-center text-center relative">
                    <div className={`w-16 h-16 ${m.color} rounded-2xl flex items-center justify-center mb-4 shadow-xl z-10`}>
                      <i className={`${m.icon} text-white text-2xl`}></i>
                    </div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{m.year}</span>
                    <h5 className="text-xl font-bold text-gray-900 mb-2">{m.label}</h5>
                    <p className="text-gray-500 text-sm leading-relaxed mb-3">{m.description}</p>
                    <span className="bg-gray-900 text-white text-xs font-bold px-4 py-1.5 rounded-full">{m.projects}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* ===== END INCUBATION MODEL SECTION ===== */}

        {/* Vision Statement */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-12 rounded-2xl shadow-2xl text-center">
            <i className="ri-rocket-line text-6xl text-[#FF8E53] mb-6"></i>
            <h3 className="text-3xl font-bold mb-6">
              رؤية التوسع
            </h3>
            <p className="text-xl leading-relaxed text-gray-300">
              نسعى لتحويل هذا المشروع إلى شبكة وطنية وإقليمية من المساحات المبتكرة، 
              تخدم آلاف الشباب ورواد الأعمال، وتصبح نموذجًا يُحتذى به في المنطقة
            </p>
            <div className="mt-8 pt-8 border-t border-gray-700 grid sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#FF6B6B] mb-1">محفظة</div>
                <div className="text-sm text-gray-400">أصول ريادية متنامية</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#4ECDC4] mb-1">استدامة</div>
                <div className="text-sm text-gray-400">دخل متجدد بدون تبرعات</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#F7971E] mb-1">أثر</div>
                <div className="text-sm text-gray-400">شباب ناجح = جمعية أقوى</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
